// src/components/GhostMode.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Ghost, RefreshCw, Crown, MessageSquare } from 'lucide-react';
import styles from './GhostMode.module.css';
// Import the ProFeatureContext
import { useProFeature } from '../context/ProFeatureContext';

/**
 * Export the hash function so it can be used in your preview.
 */
export async function hashPassword(plaintext) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const GhostMode = ({ userId, isProUser, onUpgradeClick }) => {
  // Add this line to access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [password, setPassword] = useState({ display: '', hash: '' });
  const [customMessage, setCustomMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const MAX_MESSAGE_LENGTH = 230;

  useEffect(() => {
    const fetchGhostModeState = async () => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const ghost = userSnap.data().profile?.ghostMode;
          if (ghost && !hasChanges) {
            setIsEnabled(ghost.enabled);
            setCustomMessage(ghost.customMessage || '');
            if (ghost.enabled && ghost.password && typeof ghost.password === 'object') {
              setPassword({
                display: ghost.password.display,
                hash: ghost.password.hash
              });
            } else {
              setPassword({ display: '', hash: '' });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching ghost mode state:', error);
      }
    };
    fetchGhostModeState();
  }, [userId, hasChanges]);

  // Generate a new 6-char uppercase + digit password
  const generatePassword = async () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let plaintext = '';
    for (let i = 0; i < 6; i++) {
      plaintext += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const hashed = await hashPassword(plaintext);
    setPassword({ display: plaintext, hash: hashed });
    setHasChanges(true);
  };

  const handleToggle = async () => {
    if (!isProUser) {
      // Show pro feature notification instead of immediate redirect
      showProFeatureNotification('Upgrade to Pro to enable Ghost Mode and password-protect your profile');
      return;
    }
    if (!isEnabled && !password.hash) {
      await generatePassword();
    }
    setIsEnabled(!isEnabled);
    setHasChanges(true);
  };

  const handleCustomMessageChange = (e) => {
    if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
      setCustomMessage(e.target.value);
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const updatePayload = {
        'profile.ghostMode.enabled': isEnabled,
        'profile.ghostMode.lastPasswordUpdate': new Date().toISOString(),
        'profile.ghostMode.customMessage': customMessage
      };
      if (isEnabled) {
        updatePayload['profile.ghostMode.password'] = {
          display: password.display,
          hash: password.hash
        };
      } else {
        updatePayload['profile.ghostMode.password'] = {};
      }
      // Optionally reset failed attempts on disable:
      // updatePayload['profile.ghostMode.failedAttempts'] = {};

      await updateDoc(userDocRef, updatePayload);
      setHasChanges(false);
      console.log('Ghost mode updated successfully in DB.');
    } catch (error) {
      console.error('Error updating ghost mode:', error);
    }
  };

  return (
    <div className={styles.ghostModeContainer}>
      <div className={styles.header}>
        <Ghost size={24} />
        <h1>Ghost Mode</h1>
      </div>

      <p className={styles.description}>
        Enable Ghost Mode to password-protect your profile. Visitors will need to enter a password to view it.
      </p>

      <div className={styles.controls}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={handleToggle}
            disabled={!isProUser}
          />
          <span className={styles.slider}></span>
        </label>
        <span className={styles.toggleLabel}>
          {isEnabled ? 'Ghost Mode Enabled' : 'Ghost Mode Disabled'}
        </span>
      </div>

      {!isProUser ? (
        <div className={styles.proMessage} onClick={onUpgradeClick}>
          <Crown size={16} />
          <span>Ghost Mode is a Pro feature</span>
        </div>
      ) : (
        <>
          {isEnabled && (
            <>
              <div className={styles.passwordSection}>
                <div className={styles.passwordDisplay}>
                  <span>Current Password:</span>
                  <code>{password.display || 'N/A'}</code>
                </div>
                <div className={styles.buttonGroup}>
                  <button onClick={generatePassword} className={styles.generateButton}>
                    <RefreshCw size={14} />
                    Generate New Password
                  </button>
                </div>
              </div>

              <div className={styles.messageSection}>
                <div className={styles.messageLabelContainer}>
                  <label className={styles.messageLabel}>
                    <MessageSquare size={14} />
                    <span>Custom Ghost Message:</span>
                  </label>
                  <span className={styles.charCount}>
                    {customMessage.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                <textarea
                  value={customMessage}
                  onChange={handleCustomMessageChange}
                  placeholder="This profile is Ghost-Protected..."
                  className={styles.messageInput}
                  rows={3}
                  maxLength={MAX_MESSAGE_LENGTH}
                />
                <p className={styles.messageHelp}>
                  This message displays when visitors reach your password-protected profile.
                </p>
              </div>
            </>
          )}

          {hasChanges && (
            <div className={styles.buttonGroup}>
              <button onClick={handleSave} className={styles.saveButton}>
                Save Changes
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GhostMode;